import { MemberSummaryModel, TimeUntilExpiryModel } from 'pmg-services';
import i18n, { selectedLocale } from '@/i18n';
// eslint-disable-next-line import/no-cycle
import router from '@/app/app-routes';
import { services } from '../services';

function expiringSoon(expiry) {
  const expiryDate = new Date(expiry);
  expiryDate.setMonth(expiryDate.getMonth() - 2);

  const currentDate = new Date();
  if (expiryDate <= currentDate) {
    return true;
  }

  return false;
}


const userStore = {
  state: {
    homePoints: 0,
    flexiPoints: 0,
    lessonPoints: 0,
    refreshingPoints: true, // true because it happens on mount
    userGuid: '',
    username: '',
    forename: '',
    surname: '',
    contactNumber: '',
    homeClubId: 0,
    homeClubName: '',
    membershipType: null,
    completedFirstPayment: false,
    membershipExpires: null,
    isCorporateMember: false,
    companyName: '',
    bannerUrl: '',
    thumbUrl: '',
    autoRenewOn: false,
    /** ISO timestamp when member turned off auto-renew (client + localStorage); cleared when they re-enable */
    autoRenewDisabledAt: null,
    isAdmin: false,
    isInArrears: false,
    // these 2 capital i's are upsetting
    IsExpired: false,
    IsImpersonating: false,
    refreshingMemberSummary: true, // true because it happens on mount
    refreshingMemberSummaryError: false,
    pointlessIncrement: 0,
    notPersistedIncrement: 0,
    isAuthenticated: false,
    flexiPointsAllowed: true,
    packageName: undefined,
    /** BundleId of the user's last purchased membership package (e.g. Gold/Silver) for multi-tier matrix pricing */
    memberBundleId: null,
    locale: selectedLocale,
    expiringSoon: false,
    tier: undefined,
    achievementScore: 0,
    someFormOfExperienceMembership: false,
    referralRewardEligible: true,
    canChangeHomeClub: false,
    referredBy: null,
    canUserBeReferred: false,
    additionalDetailsRequired: false,
    promptForAppReview: false,
    associates: null,
    associateSlotsOverride: 4,
    shouldBeMigratedToNine: false,
    currencySymbol: null,
    isAccountDisabled: false,
    accountDisabledReason: '',
  },

  getters: {
    // this is out of date now, it doesnt have expiry info in it
    getMemberSummary: state => new MemberSummaryModel(
      0,
      state.userGuid,
      state.username,
      state.forename,
      state.surname,
      state.contactNumber,
      state.homeClubId,
      state.homeClubName,
      state.homePoints,
      state.flexiPoints,
      state.membershipType,
      state.completedFirstPayment,
      state.membershipExpires,
      state.isCorporateMember,
      state.companyName,
      state.bannerUrl,
      true,
      state.thumbUrl,
      state.autoRenewOn,
      state.isAdmin,
      state.isInArrears,
      state.IsExpired,
      state.IsImpersonating,
      state.flexiPointsAllowed,
      state.packageName,
      state.tier,
      state.someFormOfExperienceMembership,
      state.canChangeHomeClub,
      state.promptForAppReview,
    ),

    getMemberExpiryInfo: state => new TimeUntilExpiryModel(
      state.membershipExpires,
    ),

    // Member is low on home points (<= 30). Guarded on refreshingPoints so the
    // warning doesn't flash while the initial 0 balance is still loading.
    lowOnHomePoints: state => !state.refreshingPoints && state.homePoints <= 30,

    isExperienceMember: state => (state.membershipType === 2 || state.someFormOfExperienceMembership || state.homeClubId === 555 || state.homeClubId === 590 || state.homeClubId === 196) && !state.isCorporateMember,

    // Experience and PMG Plus members can refer friends but earn no referral points themselves
    earnsReferralReward: state => state.referralRewardEligible,
  },

  mutations: {
    setMemberSummaryError(state, value) {
      state.refreshingMemberSummaryError = value;
    },
    setRefreshingPoints(state, value) {
      state.refreshingPoints = value;
    },
    setHomePoints(state, value) {
      state.homePoints = value;
    },
    setFlexiPoints(state, value) {
      state.flexiPoints = value;
    },
    setLessonPoints(state, value) {
      state.lessonPoints = value;
    },

    setAssociates(state, value) {
      if (value !== null) {
        state.associates = value.Associates;
        if (value.AssociateSlotsOverride !== null) {
          state.associateSlotsOverride = value.AssociateSlotsOverride;
        }
      }
    },

    setCanUserBeReferred(state, value) {
      state.canUserBeReferred = value && !state.someFormOfExperienceMembership;
    },

    pushMemberSummary(state, memberSummary) {
      state.userGuid = memberSummary.UserGuid;
      state.username = memberSummary.Username;
      state.forename = memberSummary.Forename;
      state.surname = memberSummary.Surname;
      state.contactNumber = memberSummary.ContactNumber;
      state.homeClubId = memberSummary.HomeClubId;
      state.homeClubName = memberSummary.HomeClubName;
      state.membershipType = memberSummary.MembershipType;
      state.completedFirstPayment = memberSummary.CompletedFirstPayment;
      state.membershipExpires = memberSummary.MembershipExpires;
      state.isCorporateMember = memberSummary.IsCorporateMember;
      state.companyName = memberSummary.CompanyName;
      state.bannerUrl = memberSummary.BannerUrl;
      state.thumbUrl = memberSummary.ThumbUrl;
      state.autoRenewOn = memberSummary.AutoRenewOn;
      if (memberSummary.AutoRenewOn) {
        state.autoRenewDisabledAt = null;
        if (typeof localStorage !== 'undefined') {
          try {
            localStorage.removeItem('pmg_autoRenewDisabledAt');
          } catch (e) {
            /* ignore */
          }
        }
      } else if (typeof localStorage !== 'undefined') {
        try {
          const stored = localStorage.getItem('pmg_autoRenewDisabledAt');
          if (stored) {
            state.autoRenewDisabledAt = stored;
          }
        } catch (e) {
          /* ignore */
        }
      }
      state.isAdmin = memberSummary.IsAdmin;
      state.isInArrears = memberSummary.IsInArrears;
      state.IsExpired = memberSummary.IsExpired;
      state.IsImpersonating = memberSummary.IsImpersonating;
      state.flexiPointsAllowed = memberSummary.FlexiPointsAllowed;
      state.packageName = memberSummary.packageName;
      state.memberBundleId = memberSummary.LastPurchasedBundleId;
      state.expiringSoon = expiringSoon(memberSummary.MembershipExpires);
      state.tier = memberSummary.Tier;
      state.someFormOfExperienceMembership = memberSummary.SomeFormOfExperienceMembership;
      state.referralRewardEligible = memberSummary.ReferralRewardEligible !== false;
      state.refreshingMemberSummaryError = false;
      state.canChangeHomeClub = memberSummary.CanChangeHomeClub;
      state.referredBy = memberSummary.ReferredBy;
      state.additionalDetailsRequired = memberSummary.AdditionalDetailsRequired;
      state.promptForAppReview = memberSummary.PromptForAppReview;
      if (memberSummary.ShouldBeMigratedToNine !== undefined) {
        state.shouldBeMigratedToNine = memberSummary.ShouldBeMigratedToNine;
      }
      if (memberSummary.CurrencySymbol) {
        state.currencySymbol = memberSummary.CurrencySymbol;
      }
      if (!this.$isMobileApp && memberSummary.IsAdmin) {
        // window.location = process.env.VUE_APP_ADMIN_URL;
      }
    },

    emptyMemberSummary(state) {
      state.userGuid = '';
      state.username = '';
      state.forename = '';
      state.surname = '';
      state.contactNumber = '';
      state.homeClubId = 0;
      state.homeClubName = '';
      state.membershipType = null;
      state.referralRewardEligible = true;
      state.completedFirstPayment = false;
      state.membershipExpires = null;
      state.isCorporateMember = false;
      state.companyName = '';
      state.bannerUrl = '';
      state.thumbUrl = '';
      state.autoRenewOn = false;
      state.autoRenewDisabledAt = null;
      if (typeof localStorage !== 'undefined') {
        try {
          localStorage.removeItem('pmg_autoRenewDisabledAt');
        } catch (e) {
          /* ignore */
        }
      }
      state.isAdmin = false;
      state.isInArrears = false;
      state.IsExpired = false;
      state.IsImpersonating = false;
      state.flexiPointsAllowed = false;
      state.packageName = undefined;
      state.memberBundleId = null;
      state.tier = undefined;
      state.someFormOfExperienceClub = false;
      state.currencySymbol = null;
      state.isAccountDisabled = false;
      state.accountDisabledReason = '';
    },
    setRefreshingMemberSummary(state, value) {
      state.refreshingMemberSummary = value;
    },
    setIsAuth(state, value) {
      state.isAuthenticated = value;
    },
    updateLocale(state, newLocale) {
      state.locale = newLocale;
    },
    setAchievementScore(state, value) {
      state.achievementScore = value;
    },
    setAccountDisabled(state, { disabled, reason }) {
      state.isAccountDisabled = disabled;
      state.accountDisabledReason = reason || '';
    },
    setAutoRenewDisabledAt(state, isoString) {
      state.autoRenewDisabledAt = isoString;
      if (typeof localStorage !== 'undefined' && isoString) {
        try {
          localStorage.setItem('pmg_autoRenewDisabledAt', isoString);
        } catch (e) {
          /* ignore */
        }
      }
    },
    clearAutoRenewDisabledTracking(state) {
      state.autoRenewDisabledAt = null;
      if (typeof localStorage !== 'undefined') {
        try {
          localStorage.removeItem('pmg_autoRenewDisabledAt');
        } catch (e) {
          /* ignore */
        }
      }
    },
  },

  actions: {
    setAuth(context, value) {
      context.commit('setIsAuth', value);
    },

    populateAssociates(context) {
      context.commit('setAssociates', null);
      services.associates.getAssociates().then((data) => {
        context.commit('setAssociates', data);
      });
    },

    populateCanUserBeReferred(context, value) {
      context.commit('setCanUserBeReferred', value);
    },

    async refreshPoints({ commit }, realtime = false) {
      commit('setRefreshingPoints', true);
      await services.member.getMemberPoints(realtime).then(
        (memberPoints) => {
          commit('setHomePoints', memberPoints.homePoints);
          commit('setFlexiPoints', memberPoints.flexiPoints);
          commit('setRefreshingPoints', false);
        },
        (error) => {
          console.log(error);
        },
      );
    },

    async refreshPointsUntilUpdate({ commit, dispatch, state }) {
      await services.member.getMemberPoints(true).then((points) => {
        if (points.homePoints !== state.homePoints || points.flexiPoints !== state.flexiPoints) {
          commit('setHomePoints', points.homePoints);
          commit('setFlexiPoints', points.flexiPoints);
        } else {
          setTimeout(() => { dispatch('refreshPointsUntilUpdate'); }, 1000);
        }
      });
    },

    async emptyMemberSummary({ commit }) {
      commit('emptyMemberSummary');
    },

    // Drives the "account disabled" banner (admin "Disable Account" feature).
    // Decoupled + error-handled so a failed lookup can never silently hide the banner.
    refreshAccountDisabledStatus(context, summary) {
      // Only an inactive member can be disabled; clear the flag otherwise.
      if (!summary || summary.IsActive !== false || !summary.UserGuid) {
        context.commit('setAccountDisabled', { disabled: false, reason: '' });
        return;
      }
      services.member.getDisabledReason(summary.UserGuid).then((data) => {
        let disabled = false;
        let reason = '';
        try {
          if (data && data.AuditData) {
            const audit = JSON.parse(data.AuditData);
            if (audit.Action === 'Disabled' && audit.Reason != null) {
              disabled = true;
              reason = audit.ReasonForUser || '';
            }
          }
        } catch (e) {
          disabled = false;
        }
        context.commit('setAccountDisabled', { disabled, reason });
      }).catch(() => {
        context.commit('setAccountDisabled', { disabled: false, reason: '' });
      });
    },

    async populateMemberSummary(context) {
      return new Promise((resolve) => {
        services.account.cookieAuthenticated().then((data) => {
          context.commit('setRefreshingPoints', true);
          context.commit('setRefreshingMemberSummary', true);
          context.commit('setMemberSummaryError', false);
          if (data) {
            services.member.memberSummary().then((summary) => {
              if (summary !== undefined) {
                context.commit('setIsAuth', true);
                context.dispatch('getClubDetails', summary.HomeClubId);

                if (summary.IsInArrears) {
                  context.commit('pushMemberSummary', summary);
                  router.push('/arrears');
                  resolve();
                } else {
                  context.commit('pushMemberSummary', summary);
                  resolve();
                }

                context.dispatch('refreshAccountDisabledStatus', summary);

                if (summary.IsCorporateMember) {
                  context.dispatch('populateAssociates');
                }

                context.commit('setHomePoints', summary.HomePoints);
                context.commit('setFlexiPoints', summary.AwayPoints);
                context.commit('setLessonPoints', summary.LessonPoints);
              } else if (context.state.isAuthenticated) {
                context.commit('setIsAuth', false);
                location.reload();
              }

              context.commit('setRefreshingPoints', false);
              context.commit('setRefreshingMemberSummary', false);
              services.member.memberAchievementScore().then((score) => {
                context.commit('setAchievementScore', score);
              });

              context.dispatch('populateShareCode');
            }).catch(() => {
              context.commit('setMemberSummaryError', true);
            });
          } else {
            context.commit('setIsAuth', false);
            context.commit('setRefreshingPoints', true);
            context.commit('setRefreshingMemberSummary', true);
            // location.reload();
          }
        });
      });
    },

    changeLocale({ commit }, newLocale) {
      i18n.locale = newLocale;
      commit('updateLocale', newLocale);
    },

  },

};

export { userStore };
